import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import {
  ETIQUETA_DE_MODULO,
  NOMBRE_DE_MODULO,
  PUNTO_DE_MODULO,
  type Modulo,
} from '@/components/layout/modulos';
import type { Pendiente } from '../lib/resumen-de-inicio';

/**
 * «Necesita tu atención» de Inicio (artefacto): lo pendiente de los tres
 * módulos en una lista, cada fila con su módulo y el botón que lleva a hacerlo.
 * Los chips filtran por módulo; sólo aparecen los de los módulos que la persona tiene.
 */
export function NecesitaTuAtencion({
  pendientes,
  modulos,
  cargando,
}: {
  pendientes: Pendiente[];
  modulos: Modulo[];
  cargando: boolean;
}) {
  const [filtro, setFiltro] = useState<Modulo | null>(null);
  const visibles = filtro ? pendientes.filter((p) => p.modulo === filtro) : pendientes;
  const chips: Array<{ clave: Modulo | null; etiqueta: string }> = [
    { clave: null, etiqueta: `Todos · ${pendientes.length}` },
    ...modulos.map((m) => ({ clave: m, etiqueta: NOMBRE_DE_MODULO[m] })),
  ];

  return (
    <section
      aria-labelledby="necesita-tu-atencion"
      className="overflow-hidden rounded-tarjeta border bg-card shadow-card"
    >
      <header className="flex flex-wrap items-end justify-between gap-3 px-5 pb-4 pt-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Hoy</p>
          <h2 id="necesita-tu-atencion" className="text-xl font-semibold tracking-tight">
            Necesita tu atención
          </h2>
        </div>
        {modulos.length > 1 && (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por módulo">
            {chips.map((c) => {
              const activo = filtro === c.clave;
              return (
                <button
                  key={c.etiqueta}
                  type="button"
                  aria-pressed={activo}
                  onClick={() => setFiltro(c.clave)}
                  className={cn(
                    'h-9 rounded-full border px-4 text-sm font-medium transition-colors',
                    activo
                      ? 'border-foreground bg-foreground text-background'
                      : 'bg-card text-muted-foreground hover:text-foreground'
                  )}
                >
                  {c.etiqueta}
                </button>
              );
            })}
          </div>
        )}
      </header>

      {cargando ? (
        <p className="border-t px-5 py-6 text-sm text-muted-foreground">
          Mirando qué quedó pendiente…
        </p>
      ) : visibles.length === 0 ? (
        <div className="flex items-center gap-3 border-t px-5 py-6 text-sm text-muted-foreground">
          <CheckCircle2 className="h-5 w-5 text-ok" aria-hidden="true" />
          {filtro
            ? `Nada pendiente en ${NOMBRE_DE_MODULO[filtro]}.`
            : 'Todo en orden: nada espera tu decisión ahora.'}
        </div>
      ) : (
        <ul>
          {visibles.map((p) => (
            <li
              key={p.id}
              className="flex flex-col gap-3 border-t px-5 py-4 sm:flex-row sm:items-center"
            >
              <span
                className={cn(
                  'mt-1.5 h-2.5 w-2.5 shrink-0 self-start rounded-full sm:mt-0 sm:self-center',
                  PUNTO_DE_MODULO[p.modulo]
                )}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 font-semibold">
                  {p.titulo}
                  <span
                    className={cn(
                      'rounded-md px-1.5 py-0.5 text-2xs font-bold uppercase tracking-wider',
                      ETIQUETA_DE_MODULO[p.modulo]
                    )}
                  >
                    {NOMBRE_DE_MODULO[p.modulo]}
                  </span>
                </p>
                <p className="text-sm text-muted-foreground">{p.detalle}</p>
              </div>
              <Link
                to={p.href}
                className="inline-flex h-10 shrink-0 items-center gap-1.5 self-start rounded-full border bg-card px-4 text-sm font-semibold transition-colors hover:bg-muted sm:self-center"
              >
                {p.accion}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
